import type { FunnelStage, FunnelSummary } from "../funnel/funnelSummary";
import { isoWeekOf } from "../filters/dateRange";
import {
  aggregateByCampaign,
  aggregateTotals,
  cpaCents,
  isGeoRow,
  type FunnelTotals,
} from "../marketing/marketingMetrics";
import { isStudioFilm } from "../echoflicks/studioFilms";
import type {
  EchoflicksVideoSession,
  MarketingMetric,
  WhatsAppBroadcast,
} from "../types";

// Pure week-over-week helpers for the executive dashboard. Kept free of React
// and the data provider so they run in node-mode vitest; every window is UTC
// and ISO-week based, matching the Marketing/Funnel pages' dateRange helpers.

const DAY_MS = 24 * 60 * 60 * 1000;
const WINDOW_TICK_MS = 5 * 60 * 1000;

export type WeekWindows = {
  /** Wall clock rounded down to five minutes so query keys stay stable. */
  now: string;
  /** Monday 00:00Z of the current ISO week. */
  thisWeekStart: string;
  /** Monday 00:00Z of the previous ISO week. */
  lastWeekStart: string;
  /** `now` minus seven days: the same point in last week. */
  lastWeekSamePoint: string;
  /** Monday 00:00Z of the coming ISO week (exclusive end of this week). */
  nextWeekStart: string;
  /** e.g. "2026-W36". */
  isoWeek: string;
};

export function startOfIsoWeekUtc(date: Date): Date {
  const start = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );
  // getUTCDay: Sunday 0 … Saturday 6; ISO weeks start on Monday.
  const offset = (start.getUTCDay() + 6) % 7;
  start.setUTCDate(start.getUTCDate() - offset);
  return start;
}

export function computeWeekWindows(now: Date = new Date()): WeekWindows {
  const rounded = new Date(
    Math.floor(now.getTime() / WINDOW_TICK_MS) * WINDOW_TICK_MS,
  );
  const thisWeekStart = startOfIsoWeekUtc(rounded);
  const lastWeekStart = new Date(thisWeekStart.getTime() - 7 * DAY_MS);
  const nextWeekStart = new Date(thisWeekStart.getTime() + 7 * DAY_MS);
  return {
    now: rounded.toISOString(),
    thisWeekStart: thisWeekStart.toISOString(),
    lastWeekStart: lastWeekStart.toISOString(),
    lastWeekSamePoint: new Date(rounded.getTime() - 7 * DAY_MS).toISOString(),
    nextWeekStart: nextWeekStart.toISOString(),
    isoWeek: isoWeekOf(thisWeekStart),
  };
}

/** `[start, end)` membership for ISO timestamps; null/empty never match. */
export function isWithin(
  iso: string | null | undefined,
  start: string,
  endExclusive: string,
): boolean {
  if (!iso) return false;
  const value = Date.parse(iso);
  if (Number.isNaN(value)) return false;
  return value >= Date.parse(start) && value < Date.parse(endExclusive);
}

export type EchoLike = Pick<
  EchoflicksVideoSession,
  | "status"
  | "has_final_video"
  | "completed_at"
  | "display_date"
  | "started_at"
  | "rating"
  | "feedback_at"
  | "occasion_label"
  | "occasion_key"
  | "echo_type"
> &
  Partial<
    Pick<
      EchoflicksVideoSession,
      "whatsapp_user_id" | "studio_agency_id" | "studio_echo_id"
    >
  > & { contact_id?: unknown };

/**
 * B4.4: Studio (agency portal) films are not B2C Echoes. They run under a
 * phantom `studio-…` user, so counting them inflated the week's Echoes and
 * the occasion mix; the home shows them as their own number instead.
 */
export function splitStudioFilms<T extends EchoLike>(
  echoes: readonly T[],
): { b2c: T[]; studio: T[] } {
  const b2c: T[] = [];
  const studio: T[] = [];
  for (const echo of echoes) (isStudioFilm(echo) ? studio : b2c).push(echo);
  return { b2c, studio };
}

export type StudioFilmsWeek = {
  /** Studio films delivered this week. */
  thisWeek: number;
  /** …and by the same point last week. */
  lastWeekToDate: number;
};

export function summarizeStudioFilms(
  studioFilms: readonly EchoLike[],
  windows: WeekWindows,
): StudioFilmsWeek {
  const { thisWeek, lastWeekToDate } = summarizeEchoes(studioFilms, windows);
  return {
    thisWeek: thisWeek.delivered,
    lastWeekToDate: lastWeekToDate.delivered,
  };
}

/** " · Studio films: 3", appended to the Echoes tile; "" in a week without any. */
export function studioFilmsSublabel(
  studio: StudioFilmsWeek,
  format: (value: number) => string = String,
): string {
  if (!studio.thisWeek && !studio.lastWeekToDate) return "";
  return ` · Studio films: ${format(studio.thisWeek)}`;
}

export function isDelivered(echo: EchoLike): boolean {
  return echo.status === "completed" || echo.has_final_video === true;
}

export function isFailed(echo: EchoLike): boolean {
  return echo.status === "failed";
}

/** When the echo was finished — the moment it counts as "made". */
export function echoDeliveredAt(echo: EchoLike): string | null {
  return echo.completed_at ?? echo.display_date ?? null;
}

/** When the echo last moved, for in-flight and failed work. */
export function echoActivityAt(echo: EchoLike): string | null {
  return echo.display_date ?? echo.completed_at ?? echo.started_at ?? null;
}

/** When the user rated it; ratings arrive after delivery so fall back forward. */
export function echoRatedAt(echo: EchoLike): string | null {
  return echo.feedback_at ?? echo.completed_at ?? echo.display_date ?? null;
}

export type Star = 1 | 2 | 3 | 4 | 5;
export const STARS: readonly Star[] = [5, 4, 3, 2, 1];

export type EchoWindowSummary = {
  delivered: number;
  failed: number;
  rated: number;
  averageRating: number | null;
  ratingDistribution: Record<Star, number>;
  /** Rated 1–3 stars: the "needs attention" bucket on the Echoes board. */
  lowRated: number;
  /** Delivered echoes that have not been rated (yet). */
  unrated: number;
};

function emptyDistribution(): Record<Star, number> {
  return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
}

function isStar(value: unknown): value is Star {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= 5
  );
}

export function summarizeEchoWindow(
  echoes: readonly EchoLike[],
  start: string,
  endExclusive: string,
): EchoWindowSummary {
  const summary: EchoWindowSummary = {
    delivered: 0,
    failed: 0,
    rated: 0,
    averageRating: null,
    ratingDistribution: emptyDistribution(),
    lowRated: 0,
    unrated: 0,
  };
  let ratingSum = 0;
  for (const echo of echoes) {
    if (
      isDelivered(echo) &&
      isWithin(echoDeliveredAt(echo), start, endExclusive)
    ) {
      summary.delivered += 1;
      if (!isStar(echo.rating)) summary.unrated += 1;
    } else if (
      isFailed(echo) &&
      isWithin(echoActivityAt(echo), start, endExclusive)
    ) {
      summary.failed += 1;
    }
    if (
      isStar(echo.rating) &&
      isWithin(echoRatedAt(echo), start, endExclusive)
    ) {
      summary.rated += 1;
      ratingSum += echo.rating;
      summary.ratingDistribution[echo.rating] += 1;
      if (echo.rating <= 3) summary.lowRated += 1;
    }
  }
  summary.averageRating =
    summary.rated > 0
      ? Math.round((ratingSum / summary.rated) * 10) / 10
      : null;
  return summary;
}

export type EchoComparison = {
  thisWeek: EchoWindowSummary;
  /** Last week, cut at the same weekday/time as now — a like-for-like baseline. */
  lastWeekToDate: EchoWindowSummary;
};

export function summarizeEchoes(
  echoes: readonly EchoLike[],
  windows: WeekWindows,
): EchoComparison {
  return {
    thisWeek: summarizeEchoWindow(
      echoes,
      windows.thisWeekStart,
      windows.nextWeekStart,
    ),
    lastWeekToDate: summarizeEchoWindow(
      echoes,
      windows.lastWeekStart,
      windows.lastWeekSamePoint,
    ),
  };
}

export type DailyBucket = {
  /** UTC calendar day, YYYY-MM-DD. */
  date: string;
  count: number;
  isThisWeek: boolean;
  isToday: boolean;
  isFuture: boolean;
};

/** Delivered echoes per UTC day across last week and this week (14 slots). */
export function dailyDeliveries(
  echoes: readonly EchoLike[],
  windows: WeekWindows,
): DailyBucket[] {
  const start = Date.parse(windows.lastWeekStart);
  const today = windows.now.slice(0, 10);
  const buckets: DailyBucket[] = Array.from({ length: 14 }, (_, index) => {
    const date = new Date(start + index * DAY_MS).toISOString().slice(0, 10);
    return {
      date,
      count: 0,
      isThisWeek: index >= 7,
      isToday: date === today,
      isFuture: date > today,
    };
  });
  for (const echo of echoes) {
    if (!isDelivered(echo)) continue;
    const at = echoDeliveredAt(echo);
    if (!at) continue;
    const index = Math.floor((Date.parse(at) - start) / DAY_MS);
    if (index >= 0 && index < buckets.length) buckets[index].count += 1;
  }
  return buckets;
}

export type OccasionShare = { label: string; count: number; share: number };

/** `wedding_anniversary` → "Wedding anniversary"; null stays null. */
function humanizeKey(key: string | null | undefined): string | null {
  if (!key) return null;
  const spaced = key.replace(/[_-]+/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : null;
}

/** Most common occasions among this week's delivered echoes. */
export function topOccasions(
  echoes: readonly EchoLike[],
  windows: WeekWindows,
  limit = 4,
): OccasionShare[] {
  const counts = new Map<string, number>();
  let total = 0;
  for (const echo of echoes) {
    if (!isDelivered(echo)) continue;
    if (
      !isWithin(
        echoDeliveredAt(echo),
        windows.thisWeekStart,
        windows.nextWeekStart,
      )
    )
      continue;
    const label =
      echo.occasion_label ??
      (echo.echo_type === "real_estate" ? "Real estate" : null) ??
      humanizeKey(echo.occasion_key) ??
      "Unknown";
    counts.set(label, (counts.get(label) ?? 0) + 1);
    total += 1;
  }
  return Array.from(counts.entries())
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, limit)
    .map(([label, count]) => ({
      label,
      count,
      share: total ? Math.round((count / total) * 100) : 0,
    }));
}

export type DeltaTone = "positive" | "negative" | "neutral";

/** Which direction is good news; failures and costs invert the colours. */
export function deltaTone(
  delta: number | null,
  goodWhen: "up" | "down" = "up",
): DeltaTone {
  if (delta == null || delta === 0) return "neutral";
  const improved = goodWhen === "up" ? delta > 0 : delta < 0;
  return improved ? "positive" : "negative";
}

/** Relative change in percent, or null when there is no baseline. */
export function percentDelta(current: number, previous: number): number | null {
  if (!previous) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Absolute change for small-range metrics such as a star average. */
export function pointDelta(
  current: number | null,
  previous: number | null,
): number | null {
  if (current == null || previous == null) return null;
  return Math.round((current - previous) * 10) / 10;
}

export type MarketingWeek = FunnelTotals & {
  costPerEchoCents: number | null;
  /** The campaign/source that brought the most new contacts. */
  topSource: { label: string; newContacts: number } | null;
};

function periodDay(row: Pick<MarketingMetric, "period">): string {
  return row.period.slice(0, 10);
}

export function summarizeMarketingWindow(
  rows: readonly MarketingMetric[],
  startDay: string,
  endDayExclusive: string,
): MarketingWeek {
  const inWindow = rows.filter((row) => {
    if (isGeoRow(row)) return false;
    const day = periodDay(row);
    return day >= startDay && day < endDayExclusive;
  });
  const totals = aggregateTotals(inWindow);
  const leader = aggregateByCampaign(inWindow)[0];
  return {
    ...totals,
    costPerEchoCents: cpaCents(totals),
    topSource:
      leader && leader.new_contacts > 0
        ? { label: leader.label, newContacts: leader.new_contacts }
        : null,
  };
}

export type MarketingComparison = {
  thisWeek: MarketingWeek;
  /** The rollup is daily, so the baseline is last week's first N full days. */
  lastWeekToDate: MarketingWeek;
};

export function summarizeMarketing(
  rows: readonly MarketingMetric[],
  windows: WeekWindows,
): MarketingComparison {
  const thisStart = windows.thisWeekStart.slice(0, 10);
  const lastStart = windows.lastWeekStart.slice(0, 10);
  // Day rows: this week covers Monday through today; the baseline covers the
  // same number of days last week, ending one day past last week's "today".
  const todayIndex = Math.floor(
    (Date.parse(windows.now) - Date.parse(windows.thisWeekStart)) / DAY_MS,
  );
  const lastEnd = new Date(
    Date.parse(windows.lastWeekStart) + (todayIndex + 1) * DAY_MS,
  )
    .toISOString()
    .slice(0, 10);
  return {
    thisWeek: summarizeMarketingWindow(
      rows,
      thisStart,
      windows.nextWeekStart.slice(0, 10),
    ),
    lastWeekToDate: summarizeMarketingWindow(rows, lastStart, lastEnd),
  };
}

export type BroadcastWeek = {
  count: number;
  recipients: number;
  delivered: number;
  read: number;
  echoesStarted: number;
};

/** Broadcasts created this week and what they achieved so far. */
export function summarizeBroadcasts(
  broadcasts: readonly Pick<
    WhatsAppBroadcast,
    | "created_at"
    | "recipient_count"
    | "delivered_count"
    | "read_count"
    | "echo_started_count"
  >[],
  windows: WeekWindows,
): BroadcastWeek {
  const summary: BroadcastWeek = {
    count: 0,
    recipients: 0,
    delivered: 0,
    read: 0,
    echoesStarted: 0,
  };
  for (const broadcast of broadcasts) {
    if (
      !isWithin(
        broadcast.created_at,
        windows.thisWeekStart,
        windows.nextWeekStart,
      )
    )
      continue;
    summary.count += 1;
    summary.recipients += broadcast.recipient_count ?? 0;
    summary.delivered += broadcast.delivered_count ?? 0;
    summary.read += broadcast.read_count ?? 0;
    summary.echoesStarted += broadcast.echo_started_count ?? 0;
  }
  return summary;
}

/** The stage keys that tell the onboarding story at a glance. */
const HEADLINE_STAGE_KEYS = [
  "conversation_started",
  "media_received",
  "echo_started",
  "echo_delivered",
  "rated",
] as const;

/** A compact funnel: the headline stages if present, else the first five. */
export function headlineFunnelStages(
  summary: Pick<FunnelSummary, "stages">,
  limit = 5,
): FunnelStage[] {
  const byKey = new Map(summary.stages.map((stage) => [stage.key, stage]));
  const headline = HEADLINE_STAGE_KEYS.map((key) => byKey.get(key)).filter(
    (stage): stage is FunnelStage => stage != null,
  );
  if (headline.length >= 3) return headline.slice(0, limit);
  return summary.stages.slice(0, limit);
}

export function formatWeekRange(
  windows: WeekWindows,
  locale: string | undefined = undefined,
): string {
  const start = new Date(windows.thisWeekStart);
  const end = new Date(Date.parse(windows.nextWeekStart) - DAY_MS);
  const dayMonth = new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  if (sameMonth) {
    const monthOnly = new Intl.DateTimeFormat(locale, {
      month: "short",
      timeZone: "UTC",
    });
    return `${start.getUTCDate()}–${end.getUTCDate()} ${monthOnly.format(end)}`;
  }
  return `${dayMonth.format(start)} – ${dayMonth.format(end)}`;
}

export function formatSignedPercent(delta: number | null): string {
  if (delta == null) return "—";
  if (delta === 0) return "±0%";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta)}%`;
}

export function formatSignedPoints(delta: number | null): string {
  if (delta == null) return "—";
  if (delta === 0) return "±0.0";
  return `${delta > 0 ? "+" : "−"}${Math.abs(delta).toFixed(1)}`;
}
